// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ResponsiveType, useState, useResponsive} from '@supermove/hooks';
import {
  PhoneNumber,
  PhoneNumberModel,
  ProductUser,
  ProjectModel,
  UserModel,
} from '@supermove/models';
import Environment from '@supermove/sdk/src/Environment';
import {colors} from '@supermove/styles';
import {openNewTab, Phone} from '@supermove/utils';

// App
import DropdownButton from '@shared/design/components/Button/DropdownButton';
import SectionedDropdownButton from '@shared/design/components/Button/SectionedDropdownButton';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import MissingCommunicationInfo from 'modules/Communication/CommunicationInbox/ProjectConversation/components/MissingCommunicationInfo';

const Container = Styled.View`
  height: 156px;
`;

const UpsellContainer = Styled.View`
  height: 156px;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const CustomerInfoContainer = Styled.View`
  align-items: center;
  margin-top: 22px;
`;

const BottomButtonsContainer = Styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 16px;
`;

const OutboundNumberContainer = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const SelectOutboundNumber = ({
  selectedOutboundNumber,
  viewer,
  handleSelectPhoneNumber,
  responsive,
}: {
  selectedOutboundNumber?: string;
  viewer: UserModel;
  handleSelectPhoneNumber: (phoneNumber: PhoneNumberModel) => void;
  responsive: ResponsiveType;
}) => {
  const dropdownOptions = PhoneNumber.getOutboundNumberDropdownOptions({
    viewer,
    handleSelectPhoneNumber,
  });

  return (
    <SectionedDropdownButton
      text={selectedOutboundNumber ? Phone.display(selectedOutboundNumber) : 'Outbound Number'}
      menuPosition={DropdownButton.MENU_POSITION.TOP}
      isSmall={false}
      menuStyle={{minWidth: 300}}
      showDescriptionInOption
      isResponsive
      isPortaled
      sheetLabel={'Select Outbound Number'}
      maxDropdownHeight={500}
      isSearchable
      sections={
        responsive.desktop
          ? dropdownOptions
          : dropdownOptions.map((section) => ({
              ...section,
              label: section.name,
            }))
      }
      ButtonComponent={TertiaryButton}
    />
  );
};

const SalesAppUpsell = () => {
  const handleRequestDemo = () => {
    openNewTab('https://www.supermove.com/ai-sales-copilot');
  };

  return (
    <UpsellContainer>
      <Text.Body style={{textAlign: 'center', color: colors.gray.secondary}}>
        See how calling works in Supermove — request a{' '}
        <Text.Link onPress={handleRequestDemo}>AI Sales Copilot</Text.Link> demo today
      </Text.Body>
    </UpsellContainer>
  );
};

const CommunicationInboxFooterSendCall = ({
  customer,
  viewer,
  refetch,
  project,
}: {
  customer: UserModel;
  viewer: UserModel;
  refetch: () => void;
  project: ProjectModel;
}) => {
  const responsive = useResponsive();
  const noPhoneNumber = !customer.phoneNumber;
  const [selectedOutboundNumber, setSelectedOutboundNumber] = useState<PhoneNumberModel | null>(
    null,
  );
  const isSalesActive = viewer.productUsers.some((productUser) =>
    ProductUser.isSalesActive(productUser),
  );

  if (!isSalesActive) {
    return <SalesAppUpsell />;
  }

  if (noPhoneNumber) {
    return (
      <MissingCommunicationInfo
        linkText={'Add a phone number'}
        text={`to make a call to ${customer.fullName}`}
        focusField='phoneNumber'
        onSuccess={refetch}
        project={project}
        user={customer}
      />
    );
  }

  const handleCallCustomer = () => {
    openNewTab(
      `${Environment.getSalesAppUrl()}/calls?page=1&userKind=ME&phoneNumber=${customer.phoneNumber}${selectedOutboundNumber ? `&outboundNumber=${selectedOutboundNumber.number}` : ''}`,
    );
  };

  return (
    <Container>
      <CustomerInfoContainer>
        <Text.Body style={{color: colors.gray.tertiary}}>{customer.fullName}</Text.Body>
        <Space height={4} />
        <Text.Subheading>{Phone.display(customer.phoneNumber)}</Text.Subheading>
      </CustomerInfoContainer>
      <Space flex={1} />
      <Line />
      <Space height={18} />
      <BottomButtonsContainer>
        <OutboundNumberContainer>
          <Text.Label style={{color: colors.gray.secondary}}>My Caller ID:</Text.Label>
          <Space width={12} />
          <SelectOutboundNumber
            selectedOutboundNumber={selectedOutboundNumber?.number}
            viewer={viewer}
            handleSelectPhoneNumber={setSelectedOutboundNumber}
            responsive={responsive}
          />
        </OutboundNumberContainer>
        <Space width={16} />
        <IconButton
          source={Icon.PhoneAlt}
          color={colors.green.status}
          onPress={handleCallCustomer}
        />
      </BottomButtonsContainer>
      <Space height={12} />
    </Container>
  );
};

CommunicationInboxFooterSendCall.fragment = gql`
  ${PhoneNumber.getOutboundNumberDropdownOptions.fragment}
  ${MissingCommunicationInfo.fragment}
  ${ProductUser.isSalesActive.fragment}

  fragment CommunicationInboxFooterSendCall_User on User {
    id
    fullName
    phoneNumber
    organizationId
    ...MissingCommunicationInfo_User
  }

  fragment CommunicationInboxFooterSendCall_Viewer on User {
    id
    productUsers {
      id
      ...ProductUser_isSalesActive
    }
    ...PhoneNumber_getOutboundNumberDropdownOptions
  }

  fragment CommunicationInboxFooterSendCall_Project on Project {
    id
    ...MissingCommunicationInfo
  }
`;

export default CommunicationInboxFooterSendCall;
